import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type {
  SoftCreditPullHistoryResponse,
  SoftCreditRepullRequest,
  SoftCreditRepullResponse,
} from "@backflip/api-types"
import { cfApi } from "~/store/server/cf/client"
import { dealsQueryKeys } from "~/store/server/cf/deals"

// History carries FICO + borrower names; prefix is in NEVER_PERSIST_KEY_PREFIXES.
export const softCreditRepullQueryKeys = {
  prefix: ["cf", "soft-credit-pulls"] as const,
  history: (dealId: string, requestId: string) =>
    [...softCreditRepullQueryKeys.prefix, dealId, requestId] as const,
}

const requestPath = (dealId: string, requestId: string) =>
  `/api/deals/${dealId}/processing-requests/${requestId}`

export const useSoftCreditPullHistory = (dealId: string, requestId: string) => {
  return useQuery({
    queryKey: softCreditRepullQueryKeys.history(dealId, requestId),
    queryFn: async () =>
      await cfApi.get<SoftCreditPullHistoryResponse>(
        `${requestPath(dealId, requestId)}/soft-credit-pulls`
      ),
    enabled: !!dealId && !!requestId,
  })
}

// Superuser-only; never retried or queued offline (each call is a new inquiry).
// onSettled refetches history, so a 409 (stale source) shows the new source.
export const useSoftCreditRepull = () => {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false,
    networkMode: "always",
    mutationFn: async ({
      dealId,
      requestId,
      sourcePullId,
    }: {
      dealId: string
      requestId: string
      sourcePullId: string
    }) =>
      await cfApi.post<SoftCreditRepullResponse>(
        `${requestPath(dealId, requestId)}/soft-credit-repull`,
        { sourcePullId } satisfies SoftCreditRepullRequest
      ),
    onSettled: (_data, _error, { dealId, requestId }) => {
      void queryClient.invalidateQueries({
        queryKey: softCreditRepullQueryKeys.history(dealId, requestId),
      })
      void queryClient.invalidateQueries({
        queryKey: dealsQueryKeys.dealDetails(dealId),
        refetchType: "active",
      })
    },
  })
}
